import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShoppingCart, User, Building2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { GENRE_STYLE } from "@/lib/genres";

const priceLabel = (n) => "$ " + (Math.round(Number(n) || 0)).toLocaleString("es-CO");

export default function BookDetailDialog({ book, open, onOpenChange, onBuy }) {
  const { addItem } = useCart();
  if (!book) return null;
  const gs = GENRE_STYLE[book.genre] || { color: "#5b6478", bg: "#f1efe9" };
  const outOfStock = typeof book.stock === "number" && book.stock <= 0;

  const handleAdd = () => {
    addItem(book);
    toast.success(`"${book.title}" agregado al carrito`);
  };

  const handleBuy = () => {
    onBuy?.(book);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-white p-0 overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="book-detail-dialog">
        <DialogTitle className="sr-only">{book.title}</DialogTitle>
        <DialogDescription className="sr-only">Detalle del libro {book.title} de {book.author}</DialogDescription>

        <div className="flex flex-col sm:flex-row">
          {/* Portada */}
          <div className="sm:w-[230px] shrink-0 bg-[var(--lc-cream)] p-6 grid place-items-center">
            <img
              src={book.image_url}
              alt={`Portada de ${book.title}`}
              className="rounded-2xl w-40 sm:w-full max-w-[190px] object-cover shadow-[0_16px_36px_-10px_rgba(27,42,91,0.3)]"
            />
          </div>

          {/* Información (se extiende hacia la derecha) */}
          <div className="flex flex-col flex-1 min-w-0 p-6 sm:p-8">
            <span
              className="self-start inline-block rounded-full px-3 py-1 text-xs font-semibold mb-3"
              style={{ background: gs.bg, color: gs.color }}
            >
              {book.genre}
            </span>

            <h2 className="font-display font-bold text-2xl sm:text-3xl text-[var(--lc-ink)] leading-tight" data-testid="detail-title">
              {book.title}
            </h2>

            <div className="mt-3 space-y-1.5 text-sm text-[var(--lc-slate)]">
              <p className="flex items-center gap-2"><User className="w-4 h-4 text-[var(--lc-primary)]" /> {book.author}</p>
              {book.publisher && (
                <p className="flex items-center gap-2" data-testid="detail-publisher">
                  <Building2 className="w-4 h-4 text-[var(--lc-primary)]" /> Editorial: {book.publisher}
                </p>
              )}
            </div>

            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-wide text-[var(--lc-slate)] mb-1">Sinopsis</p>
              <p className="text-[15px] text-[var(--lc-ink)] leading-relaxed whitespace-pre-line" data-testid="detail-synopsis">
                {book.synopsis}
              </p>
            </div>

            <div className="mt-auto pt-6">
              <div className="flex items-center gap-3 mb-3">
                <p className="font-display font-bold text-2xl text-[var(--lc-ink)]">
                  {priceLabel(book.price)}
                </p>
                {outOfStock && (
                  <span data-testid="detail-agotado-badge" className="rounded-full bg-[var(--lc-accent)] text-white text-xs font-bold px-3 py-1">
                    Agotado
                  </span>
                )}
              </div>
              {outOfStock ? (
                <>
                  <p className="text-sm text-[var(--lc-slate)] mb-3">
                    Este libro está agotado. Puedes <strong>encargarlo</strong> y lo conseguiremos para ti; tu pago queda registrado como encargo.
                  </p>
                  <button
                    onClick={handleBuy}
                    data-testid="detail-preorder"
                    className="w-full flex items-center justify-center gap-2 rounded-full bg-[var(--lc-accent)] hover:opacity-90 text-white py-3 font-display font-semibold text-base transition-opacity shadow-[0_10px_20px_-4px_rgba(225,70,90,0.5)]"
                  >
                    <CreditCard className="w-5 h-5" />
                    Encargar
                  </button>
                </>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={handleAdd}
                    data-testid="detail-add-to-cart"
                    className="flex-1 flex items-center justify-center gap-2 rounded-full border-2 border-[var(--lc-primary)] text-[var(--lc-primary)] bg-white hover:bg-[var(--lc-primary)]/5 py-3 font-display font-semibold text-base transition-colors"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    Agregar
                  </button>
                  <button
                    onClick={handleBuy}
                    data-testid="detail-buy-now"
                    className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[var(--lc-primary)] hover:bg-[var(--lc-primary-hover)] text-white py-3 font-display font-semibold text-base transition-colors shadow-[0_10px_20px_-4px_rgba(225,70,90,0.5)]"
                  >
                    <CreditCard className="w-5 h-5" />
                    Comprar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
